import { NgClass, NgOptimizedImage, NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, HostBinding, Input, OnInit } from '@angular/core';

import { CRMTokenImages, CurrencyConfig, SitecoreTokenCardDetailsConfig } from '@frontend/sports/common/client-config-data-access';
import { DateProviderService } from '@frontend/sports/common/core/utils/date';
import { interpolateDoubleBracesString } from '@frontend/sports/common/core/utils/string';
import { ActiveModal } from '@frontend/sports/modal/feature';
import { UserService } from '@frontend/sports/user/feature';
import { DeviceService, IntlService } from '@frontend/vanilla/core';
import { TrustAsHtmlPipe } from '@frontend/vanilla/shared/browser';
import { IconCustomComponent } from '@frontend/vanilla/shared/icons';
import { NgScrollbarModule } from 'ngx-scrollbar';
import { RewardTokenImages, RewardTokenType } from 'packages/sports/web/app/src/tokens-base/token-base.models';

import { CurrencyPipe } from '../../../../../base/pipes/currency.pipe';
import { FreeBetToken, IRewardToken, VIPExclusiveDetails } from '../../../reward-tokens.model';
import { RewardTokensBannerService, TokenCategories } from '../../../services/reward-tokens-banner.service';
import { RewardTokensCardDetailsService } from '../../../services/reward-tokens-card-details.service';

@Component({
    selector: 'bs-token-quick-view-details',
    templateUrl: './token-quick-view-details.component.html',
    styleUrl: '../cards/styles/reward-token-card-shared.scss',
    providers: [RewardTokensCardDetailsService],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [NgClass, NgOptimizedImage, CurrencyPipe, NgScrollbarModule, NgTemplateOutlet, IconCustomComponent, TrustAsHtmlPipe],
})
export class TokenQuickViewDetailsComponent implements OnInit {
    @Input() token: IRewardToken;
    @Input() categories: TokenCategories;
    @Input() promoHubUrlTokenType: string;
    @Input() tokenTitle: string;
    @Input() tokenInfoUrl: string;
    @HostBinding('class') className = 'reward-tokens-module';
    private readonly SEVEN_DAYS_COUNT = 7;
    private readonly TODAY_COUNT = 2;
    prependCurrencySymbol: boolean;
    qualifyList: string[];
    description: string;
    expireTokenInDays: string;
    isRightTimerDisable: boolean;
    isExpiredToday: boolean;
    isFreebetToken: boolean;
    isMobileDevice: boolean;
    tokenType: string;
    vipExclusiveContent: VIPExclusiveDetails;
    isVipExclusive?: boolean;

    constructor(
        private activeModal: ActiveModal,
        private rewardTokensBannerService: RewardTokensBannerService,
        protected intlService: IntlService,
        private currencyConfig: CurrencyConfig,
        public userService: UserService,
        private cardDeatilsService: RewardTokensCardDetailsService,
        private crmTokenimage: CRMTokenImages,
        public sitecoreCardDetailsConfig: SitecoreTokenCardDetailsConfig,
        private deviceService: DeviceService,
    ) {
        this.isMobileDevice = this.deviceService.isMobilePhone;
    }

    ngOnInit(): void {
        this.prependCurrencySymbol = this.currencyConfig.currencySymbolDisplayList.includes(this.userService.currency);
        this.qualifyList = this.cardDeatilsService.tokenToQualifyData(this.token, this.categories);
        this.description = this.cardDeatilsService.tokenCustomDescription(this.token);
        this.expireTokenInDays = this.rewardTokensBannerService.getTokenExpires(this.token.expiryDate)?.split(',')[0];
        this.isRightTimerDisable = this.token.expiryDate < DateProviderService.daysFromNow(this.SEVEN_DAYS_COUNT);
        this.isExpiredToday = this.token.expiryDate < DateProviderService.daysFromNow(this.TODAY_COUNT);
        this.isFreebetToken = this.token.rewardTokenType === RewardTokenType.FreeBet && !this.token.customTitle;
        this.tokenType = this.cardDeatilsService.getTokenType(this.token.rewardTokenType);
        this.vipExclusiveContent = this.cardDeatilsService.getVIPExclusiveContent();
        this.isVipExclusive = this.cardDeatilsService.isVIPBadgeEnabled() && this.token.isVipExclusive;
    }

    setTokenInfoUrl(): void {
        this.rewardTokensBannerService.setTokenInfoUrl(this.promoHubUrlTokenType, this.token.userTokenId);
    }

    closeDialog(): void {
        this.activeModal.close();
    }

    betNowClick(): void {
        this.activeModal.close('betnow-clicked');
    }

    get expiresDateAndTime(): string {
        return interpolateDoubleBracesString(this.sitecoreCardDetailsConfig.messages?.expiresTokenTemplate, {
            expiresDate: this.intlService.formatDate(this.token.expiryDate, 'MM/dd/yy'),
            expiresTime: this.intlService.formatDate(this.token.expiryDate, 'hh:mm a'),
        });
    }

    get clockIcon(): string {
        return this.crmTokenimage.crmTokenImages.find((x) => x.name === RewardTokenImages.RewardTokenTimerBlack)?.src || '';
    }

    get tokenIcon(): string {
        const rewardsIcons = this.rewardTokensBannerService.getIconUrl();

        return rewardsIcons[this.token.rewardTokenType];
    }

    get amount() {
        return (this.token as FreeBetToken).amount;
    }
}
